/* External dependencies */
import update from 'immutability-helper';

/* Local dependencies */
import { setNestedValue } from '../../../common/set-nested-value';
import { serviceProviderInput } from '../../utils/helper';
import { Preference, ProcessService, Provider } from '../../utils/types';
import { ProcessServiceAction, ProcessServiceActionTypes } from './actions';

export interface ProcessServiceProviderErrors {
  serviceProviderId: Error | null;
  serviceId: Error | null;
  minAmount?: Error | null;
  maxAmount?: Error | null;
  currency?: Error | null;
  clientFees?: Error | null;
  serviceRewardFees?: Error | null;
}

export interface ProcessServiceErrors {
  maxAmount: any;
  minAmount: any;
  id: Error | null;
  name_en?: Error | null;
  name_ky?: Error | null;
  name_ru?: Error | null;
  name_tr?: Error | null;
  maxDigits?: Error | null;
  minDigits?: Error | null;
  placeholder_en?: Error | null;
  placeholder_ky?: Error | null;
  placeholder_ru?: Error | null;
  placeholder_tr?: Error | null;
  rboCode?: Error | null;
  tags?: Error | null;
  requiredFieldsProvider?: Error | null;
  serviceProviders?: ProcessServiceProviderErrors[];
  requiredFieldsServiceId?: Error | null;
}

export interface ProcessServiceState {
  loading?: boolean;
  service?: ProcessService;
  serviceErrors?: ProcessServiceErrors;
  validationPassed: boolean;
  justValidated?: boolean;
  preferences?: Preference[];
}

export const initialProcessServiceState: ProcessServiceState = {
  service: {
    id: '',
    exactAmountRequired: false,
    name_en: '',
    name_ru: '',
    name_ky: '',
    tags: [],
    name_tr: '',
    maxDigits: 0,
    minDigits: 0,
    placeholder_en: '',
    placeholder_ky: '',
    placeholder_ru: '',
    placeholder_tr: '',
    rboCode: '',
    serviceProviders: [serviceProviderInput],
    preference: '',
    preferences: [],
    requiredFieldsProvider: 'PAY24',
  },
  serviceErrors: {
    maxAmount: null,
    minAmount: null,
    name_en: null,
    name_ky: null,
    name_ru: null,
    name_tr: null,
    maxDigits: null,
    minDigits: null,
    maxAmount: null,
    minAmount: null,
    requiredFieldsProvider: null,
    placeholder_en: null,
    placeholder_ky: null,
    placeholder_ru: null,
    placeholder_tr: null,
    rboCode: null,
    serviceProviders: [
      {
        serviceProviderId: null,
        serviceId: null,
        minAmount: null,
        maxAmount: null,
        currency: null,
        clientFees: null,
        serviceRewardFees: null,
      },
    ],
    id: null,
    tags: null,
  },
  validationPassed: false,
  justValidated: false,
};

export default function ProcessServiceReducer(state = initialProcessServiceState, action: ProcessServiceAction) {
  switch (action.type) {
    case ProcessServiceActionTypes.RESET_CREATE_SERVICE_STATE:
      return initialProcessServiceState;

    case ProcessServiceActionTypes.UPDATE_SERVICE_FIELDS:
      const [name, value] = action.updates;

      return update(state, {
        service: {
          $set: update(state.service, {
            [name]: { $set: value },
          }),
        },
        validationPassed: { $set: false },
        justValidated: { $set: false },
        serviceErrors: {
          $set: update(state.serviceErrors, {
            [name]: { $set: null },
          }),
        },
      });

    case ProcessServiceActionTypes.CHANGE_SERVICE_PROVIDER_FIELDS:
      const { serviceProvider } = action;
      const field = Object.keys(serviceProvider)[0];
      const val = serviceProvider[field as keyof typeof serviceProvider];

      const result = setNestedValue(state.service?.serviceProviders?.[action.index] || {}, field, val);

      const serviceProviders = [...(state.service?.serviceProviders || [])];
      serviceProviders[action.index] = result;

      let newState = {
        ...state,
        service: {
          ...state.service,
          serviceProviders,
        },
        validationPassed: false,
        justValidated: false,
      };

      return newState;

    case ProcessServiceActionTypes.ADD_SELECT_FIELD_FOR_SERVICE_PROVIDER_SERVICE:
      const serviceProvidersErrorsCount =
        state.service.serviceProviders.length - state.serviceErrors.serviceProviders?.length;

      const serviceProvidersErrors = Array(serviceProvidersErrorsCount + 1).fill({
        serviceProviderId: null,
        serviceId: null,
        minAmount: null,
        maxAmount: null,
        currency: null,
        clientFees: null,
        serviceRewardFees: null,
      });

      return update(state, {
        service: {
          $set: update(state.service, {
            serviceProviders: {
              $set: update(state.service?.serviceProviders, {
                $push: [serviceProviderInput],
              }),
            },
          }),
        },
        serviceErrors: {
          $set: update(state.serviceErrors, {
            serviceProviders: {
              $set: update(state?.serviceErrors?.serviceProviders, {
                $push: serviceProvidersErrors,
              }),
            },
          }),
        },
      });

    case ProcessServiceActionTypes.REMOVE_SELECT_FIELD_FOR_SERVICE_PROVIDER_SERVICE:
      return update(state, {
        service: {
          $set: update(state.service, {
            serviceProviders: {
              $set: update(state.service.serviceProviders, {
                $splice: [[action.index, 1]],
              }),
            },
          }),
        },
        serviceErrors: {
          $set: update(state.serviceErrors, {
            serviceProviders: {
              $set: update(state.serviceErrors.serviceProviders, {
                $splice: [[action.index, 1]],
              }),
            },
          }),
        },
      });

    case ProcessServiceActionTypes.SET_SERVICE_ERRORS: {
      return update(state, {
        serviceErrors: { $set: action.errors },
        validationPassed: { $set: action.validationPassed },
        justValidated: { $set: true },
      });
    }

    case ProcessServiceActionTypes.RESET_VALIDATION: {
      return update(state, {
        validationPassed: { $set: false },
      });
    }

    case ProcessServiceActionTypes.SET_SERVICE_INITIAL_STATE: {
      const serviceProvidersCountArray = new Array(action.service.serviceProviders.length);

      if (!action.service.requiredFieldsProvider) {
        action.service.requiredFieldsProvider = Provider.DEMIR_BANK;
      }

      return update(state, {
        service: {
          $set: {
            ...action.service,
            preference: '',

            preferences: action.service.preferences ?? [],
          },
        },
        serviceErrors: {
          serviceProviders: {
            $set: serviceProvidersCountArray.map(() => ({
              serviceId: null,
              serviceProviderId: null,
              minAmount: null,
              maxAmount: null,
              currency: null,
              clientFees: null,
              serviceRewardFees: null,
            })),
          },
        },
        justValidated: { $set: false },
      });
    }

    default:
      return state;
  }
}
